import { ChangeEvent, useState, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { SearchInput } from 'UIPackage/MainModule/components/SearchInput';
import { FilterCheckbox } from 'UIPackage/MainModule/components/FilterCheckbox';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { Button } from 'UIPackage/MainModule/components/Button';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { AgreementListFiltersModal } from 'AgreementListPackage/AgreementListFiltersModule/components/AgreementListFiltersModal';
import { agreementListFilterSetSwitcher } from 'AgreementListPackage/AgreementListFiltersModule/actions/agreementListFilterActions';
import { getAgreementListFilterIsFilterOn } from 'AgreementListPackage/AgreementListFiltersModule/selectors/agreementListFilterSelectors';
import {
  agreementListGetData,
  agreementListSetSearchValue,
} from 'AgreementListPackage/MainModule/actions/agreementListActions';
import { getAgreementListSearchValue } from 'AgreementListPackage/MainModule/selectors/agreementListSelectors';

import styles from './AgreementListPanel.module.scss';

interface IAgreementListPanelProps {
  dataCount: number;
  searchInputValue: string;
  isFilterOn: boolean;
}

const AgreementListPanelWithoutStore = ({ dataCount, searchInputValue, isFilterOn }: IAgreementListPanelProps) => {
  const [isFiltersModalVisible, setIsFiltersModalVisible] = useState(false);
  const [isSearchInputValid, setIsSearchInputValid] = useState(false);
  const [searchInputError, setSearchInputError] = useState('');
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const handleFilterCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(agreementListFilterSetSwitcher(e.target.checked));
      dispatch(agreementListGetData());
    },
    [dispatch],
  );

  const handleFilterButtonClick = useCallback(() => {
    setIsFiltersModalVisible(true);
  }, []);

  const closeFiltersModal = useCallback(() => setIsFiltersModalVisible(false), []);

  const handleSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      dispatch(agreementListSetSearchValue(String(value)));
      const isValid = value.length > 2 || value.length === 0;

      setIsSearchInputValid(isValid);
    },
    [dispatch],
  );

  const handleSearchInputSubmit = useCallback(() => {
    if (!isSearchInputValid) {
      setSearchInputError('Длина строки должна быть не менее 3 символов');
    } else {
      setSearchInputError('');
      dispatch(agreementListGetData());
    }
  }, [dispatch, isSearchInputValid]);

  const handleOnBlur = useCallback(() => {
    !isSearchInputValid ? setSearchInputError('Длина строки должна быть не менее 3 символов') : setSearchInputError('');
  }, [isSearchInputValid]);

  const filterButtonAttributes = {
    ...(isFilterOn && { 'data-active': true }),
  };

  return (
    <div className="shared-panel-container">
      <div className={styles.Actions}>
        <SearchInput
          className={styles.SearchInput}
          value={searchInputValue}
          name="searchString"
          placeholder="Поиск"
          buttonDisabled={!isSearchInputValid}
          error={searchInputError}
          onChange={handleSearchInputChange}
          onSubmit={handleSearchInputSubmit}
          onBlur={handleOnBlur}
        />
        <Tooltip
          placement="right"
          content={<div className={styles.TooltipContent}>Введите номер соглашения или номер мероприятия</div>}
        >
          <span className={styles.TooltipIcon} />
        </Tooltip>
      </div>
      <div className={styles.Filters}>
        <p className={styles.CurrentFilter}>
          Соглашения моей организации&nbsp;
          <small className={styles.CurrentFilterNumbers}>({dataCount})</small>
        </p>
        <FilterCheckbox
          className={styles.FilterCheckbox}
          id="filterToggler"
          name="filterToggler"
          checked={isFilterOn}
          onChange={handleFilterCheckboxChange}
        />
        <Button
          className={styles.FilterButton}
          text="Фильтр..."
          color="info"
          onClick={handleFilterButtonClick}
          {...filterButtonAttributes}
        />
      </div>
      {isFiltersModalVisible && (
        <AgreementListFiltersModal visible={isFiltersModalVisible} onClose={closeFiltersModal} />
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  searchInputValue: getAgreementListSearchValue(state),
  isFilterOn: getAgreementListFilterIsFilterOn(state),
});

export const AgreementListPanel = connect(mapStateToProps)(AgreementListPanelWithoutStore);
